if (!customElements.get('rs7gt-launch')) {
  customElements.define('rs7gt-launch', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.animations = [];
      this.lastScroll = window.scrollY;
      const options = { signal: this.controller.signal };
      this.motionQuery.addEventListener('change', () => this.updateMotion(), options);
      window.addEventListener('scroll', () => this.requestScroll(), { ...options, passive: true });
      window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.refresh(), 160);
      }, options);
      window.addEventListener('pageshow', () => this.refresh(), options);
      window.addEventListener('load', () => this.refresh(), options);
      window.addEventListener('hashchange', () => this.requestScroll(), options);
      document.addEventListener('visibilitychange', () => {
        this.updateVideo();
        this.syncDesignStep();
      }, options);
      this.addEventListener('focusin', () => this.showHeader(), options);
      this.addEventListener('focusout', () => this.requestScroll(), options);
      ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(name => {
        document.addEventListener(name, () => this.requestMount(), options);
      });
      document.addEventListener('shopify:block:select', () => {
        this.showHeader();
        this.refresh();
      }, options);
      document.fonts?.ready.then(() => {
        if (!options.signal.aborted) this.refresh();
      });
      this.mount();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.releaseContent();
      clearTimeout(this.resizeTimer);
      cancelAnimationFrame(this.mountFrame);
      cancelAnimationFrame(this.scrollFrame);
      this.mountFrame = this.scrollFrame = 0;
      this.removeAttribute('data-motion-paused');
      this.removeAttribute('data-header-hidden');
      this.removeAttribute('data-header-light');
    }

    requestMount() {
      if (this.mountFrame || !this.isConnected) return;
      this.mountFrame = requestAnimationFrame(() => {
        this.mountFrame = 0;
        this.mount();
      });
    }

    releaseContent() {
      this.chapters?.destroy();
      this.chapters = null;
      this.pager?.destroy();
      this.pager = null;
      this.contentController?.abort();
      this.heroObserver?.disconnect();
      this.destroyMotion();
      this.stopVideo();
    }

    mount() {
      const pageKey = this.pager?.stops[this.pager.currentIndex()]?.key;
      this.releaseContent();
      this.contentController = new AbortController();
      const options = { signal: this.contentController.signal };
      this.header = this.querySelector('[data-launch-header]');
      this.clock = this.querySelector('rs7gt-launch-clock');
      this.hero = this.querySelector('[data-launch-hero]');
      this.media = this.querySelector('[data-launch-media]');
      this.mediaLayers = [...(this.media?.children || [])].filter(element => element.matches('picture, video'));
      this.heroTitle = this.querySelector('[data-launch-title]');
      this.design = this.querySelector('[data-launch-design]');
      this.lightSections = [this.design, ...this.querySelectorAll('[data-launch-light-surface]')].filter(Boolean);
      this.banner = this.querySelector('rs7gt-launch-banner');
      this.stage = this.querySelector('[data-launch-design-stage]');
      this.designTitle = this.querySelector('[data-design-title]');
      this.designTitleReveal = this.designTitle?.querySelector('[data-clock-reveal]');
      this.designCopy = this.querySelector('[data-design-copy]');
      this.copyReveals = [...(this.designCopy?.querySelectorAll('[data-clock-manual]') || [])];
      this.scrollCue = this.querySelector('.rs7-launch-scroll-cue');
      this.video = this.querySelector('[data-launch-video]');
      this.videoFailed = false;
      this.heroVisible = false;
      this.editor = this.dataset.designMode === 'true';
      if (window.RS7GTChapters) this.chapters = new window.RS7GTChapters(this);
      if (!this.editor && window.RS7GTPageScroll) this.pager = new window.RS7GTPageScroll(this);
      if (this.pager && pageKey) {
        this.pager.restoreKey = pageKey;
        this.pager.initialized = true;
      }
      this.querySelectorAll('img:not([data-rotator-image])').forEach(img => {
        if (!img.complete) img.addEventListener('load', () => this.refresh(), options);
      });
      try {
        this.mediaConfig = JSON.parse(this.querySelector('[data-launch-media-config]')?.textContent || '{}');
      } catch (_) {
        this.mediaConfig = {};
      }
      this.video?.addEventListener('playing', () => {
        if (this.canPlayVideo()) this.video.hidden = false;
        else this.stopVideo();
      }, options);
      this.video?.addEventListener('error', () => {
        this.videoFailed = true;
        this.stopVideo();
      }, options);
      if (this.hero && 'IntersectionObserver' in window) {
        this.heroObserver = new IntersectionObserver(entries => {
          this.heroVisible = entries[0].isIntersecting;
          this.updateVideo();
        });
        this.heroObserver.observe(this.hero);
      }
      this.updateMotion();
      this.showHeader();
    }

    requestScroll() {
      if (this.scrollFrame || !this.isConnected) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        const y = window.scrollY;
        this.pager?.updateCoverage(y);
        this.syncDesignStep();
        this.clock?.updateProgress?.(y, this.pager?.stops || this.clockStops);
        this.updateHeaderTone(y);
        this.updateHeroCoverage(y);
        this.chapters?.update();
        const delta = y - this.lastScroll;
        const locked = this.editor || this.header?.contains(document.activeElement) || this.hasAttribute('data-chapters-open');
        if (locked || y < this.headerHideStart) this.setHeaderHidden(false);
        if (Math.abs(delta) < 4) return;
        this.setHeaderHidden(!locked && y >= this.headerHideStart && delta > 0);
        this.lastScroll = y;
      });
    }

    showHeader() {
      this.setHeaderHidden(false);
      this.lastScroll = window.scrollY;
      this.updateHeaderTone(this.lastScroll);
    }

    setHeaderHidden(hidden) {
      this.toggleAttribute('data-header-hidden', hidden);
      this.header?.classList.toggle('is-hidden', hidden);
    }

    flowTop(element) {
      const top = element.getBoundingClientRect().top;
      const wrapper = element.closest('#MainContent > .shopify-section');
      if (this.hasAttribute('data-page-overlap') && wrapper?.hasAttribute('data-page-layer')) {
        return wrapper.getBoundingClientRect().top + window.scrollY
          + top - wrapper.firstElementChild.getBoundingClientRect().top;
      }
      return top + window.scrollY;
    }

    sceneVisibility(element) {
      return this.pager?.visibilityFor(element) ?? 1;
    }

    measureHeaderBoundary() {
      if (!this.pager) {
        // The editor uses native flow; measure its section positions only on
        // layout refresh, not while updating the clock on scroll.
        const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        this.clockStops = [...this.querySelectorAll('#MainContent > .shopify-section')]
          .filter(wrapper => wrapper.offsetHeight && wrapper.querySelector('section'))
          .map((wrapper, index) => ({ y: index ? Math.max(0, Math.min(max, wrapper.getBoundingClientRect().top + window.scrollY)) : 0 }))
          .filter((stop, index, all) => !index || stop.y > all[index - 1].y);
      }
      const bounds = this.design?.getBoundingClientRect();
      this.designStart = bounds ? this.flowTop(this.design) : Infinity;
      this.secondChapterEnd = bounds ? this.designStart + bounds.height : Infinity;
      this.lightSectionBounds = this.lightSections.map(section => {
        const rect = section.getBoundingClientRect();
        const start = this.flowTop(section);
        return { start, end: start + rect.height };
      });
      this.headerHideStart = this.header?.querySelector('.rs7-launch-header')?.offsetHeight || 0;
      this.headerCenter = (this.header?.offsetHeight || 0) / 2;
      this.updateHeaderTone(window.scrollY);
      this.updateHeroCoverage(window.scrollY);
    }

    updateHeaderTone(y) {
      const position = y + this.headerCenter;
      this.toggleAttribute('data-header-light', !!this.lightSectionBounds?.some(bounds => position >= bounds.start && position < bounds.end));
    }

    updateHeroCoverage(y) {
      const covered = this.hasAttribute('data-design-story') && y >= this.designStart;
      if (covered === this.heroCovered) return;
      this.heroCovered = covered;
      this.updateVideo();
    }

    updateMotion() {
      if (this.updatingMotion) return;
      this.updatingMotion = true;
      this.pager?.prepareRefresh(true);
      this.destroyMotion();
      const enabled = !this.motionQuery.matches && !this.editor;
      this.toggleAttribute('data-motion-paused', !enabled);
      this.dispatchEvent(new CustomEvent('rs7gt:motionchange', {
        bubbles: true,
        detail: { paused: !enabled, reduced: this.motionQuery.matches, enabled }
      }));
      if (enabled && window.gsap && window.ScrollTrigger) {
        try {
          window.gsap.registerPlugin(window.ScrollTrigger);
          this.createMotion();
        } catch (_) {
          this.destroyMotion();
        }
      }
      if (enabled && window.RS7GTClockReveal) this.clockReveal = new window.RS7GTClockReveal(this);
      if (enabled && this.designTitleReveal) {
        // Follow the actual text completion, including font fallback and paused
        // tabs, instead of requiring another gesture or guessing a fixed delay.
        this.designTitleObserver = new MutationObserver(() => this.requestScroll());
        this.designTitleObserver.observe(this.designTitleReveal, {
          attributes: true, attributeFilter: ['data-clock-complete', 'class']
        });
      }
      this.refresh();
      this.updateVideo();
      this.updatingMotion = false;
    }

    createMotion() {
      const gsap = window.gsap;
      if (!this.readStoryLayout()) return;
      this.setAttribute('data-design-story', '');
      this.storyLayout = this.readStoryLayout();
      if (!this.storyLayout) {
        this.removeAttribute('data-design-story');
        return;
      }
      this.applyStoryLayout();
      this.pager?.measureOverlap();

      const cover = gsap.timeline({ scrollTrigger: {
        trigger: this.design, start: () => this.flowTop(this.design) - window.innerHeight,
        end: () => this.flowTop(this.design), scrub: 0.45,
        invalidateOnRefresh: true
      } });
      this.animations.push(cover);
      if (this.mediaLayers.length) cover.fromTo(this.mediaLayers, { scale: 1 }, { scale: 1.035, duration: 1, ease: 'none' }, 0);
      if (this.scrollCue) cover.fromTo(this.scrollCue, { autoAlpha: 1 }, { autoAlpha: 0, duration: 0.2, ease: 'none' }, 0);

      this.designExpanded = false;
      gsap.set(this.designCopy, { autoAlpha: 0, y: 12 });
      this.designTimeline = gsap.timeline({ paused: true });
      this.animations.push(this.designTimeline);
      this.designTimeline.fromTo(this.designTitle, { autoAlpha: 1, yPercent: -50, y: 0 },
        { yPercent: -50, y: () => -this.storyLayout.lift, duration: 0.6, ease: 'power3.out' }, 0)
        .fromTo(this.designCopy, { autoAlpha: 0, y: 12 },
          { autoAlpha: 1, y: 0, duration: 0.8, ease: 'power2.out' }, 0.3)
        .call(() => {
          this.designExpanded = true;
          this.revealDesignCopy();
        }, null, 0.3);
    }

    syncDesignStep() {
      if (!this.design) return;
      const titlePending = this.designTitleReveal?.classList.contains('is-clock-prepared');
      if (!this.designTimeline) {
        // Compact viewports use the readable static composition, with the same
        // title-before-copy reveal when the text animation is available.
        if (!titlePending && this.copyReveals.some(element => element.classList.contains('is-clock-prepared'))) {
          this.designExpanded = true;
          this.revealDesignCopy();
        }
        return;
      }
      const visibility = this.sceneVisibility(this.design);
      if (visibility <= 0) {
        this.designTimeline.pause(0, true);
        this.designActive = this.designExpanded = false;
      } else if (visibility < 0.6 || document.hidden || titlePending) {
        this.designTimeline.pause();
      } else if (!this.designActive) {
        this.designActive = true;
        this.designTimeline.play(0);
      } else if (this.designTimeline.progress() < 1) {
        this.designTimeline.resume();
      }
    }

    readStoryLayout() {
      if (!this.hero || !this.design || !this.stage || !this.designTitle || !this.designCopy) return null;
      // The visible stage uses the viewport, independently of the story's scroll range.
      const height = window.innerHeight;
      const mobile = window.innerWidth < 750;
      if (height < (mobile ? 520 : 600)) return null;
      const titleHeight = this.designTitle.offsetHeight;
      const copyHeight = this.designCopy.offsetHeight;
      if (!titleHeight || !copyHeight) return null;
      const headerHeight = this.header?.offsetHeight || 0;
      const gap = mobile ? Math.max(24, Math.min(32, height * 0.04)) : Math.max(24, Math.min(40, height * 0.035));
      let lift = height * 0.12;
      if (mobile) {
        const top = headerHeight + Math.max(28, height * 0.04);
        const bottom = height - Math.max(32, height * 0.06);
        const contentHeight = titleHeight + gap + copyHeight;
        if (contentHeight > bottom - top) return null;
        const contentTop = top + (bottom - top - contentHeight) / 2;
        lift = height / 2 - contentTop - titleHeight / 2;
      }
      const copyTop = titleHeight / 2 - lift + gap;
      const titleTop = height / 2 - lift - titleHeight / 2;
      const copyBottom = height / 2 + copyTop + copyHeight;
      if (titleTop < headerHeight + 28 || copyBottom > height - Math.max(36, height * 0.06)) return null;
      return { height, lift, copyTop };
    }

    applyStoryLayout() {
      this.style.setProperty('--rs7-launch-copy-top', `${this.storyLayout.copyTop}px`);
      this.style.setProperty('--rs7-launch-hero-top', `${Math.min(0, this.storyLayout.height - this.hero.offsetHeight)}px`);
    }

    revealDesignCopy() {
      if (!this.designExpanded) return;
      this.copyReveals.forEach(element => this.clockReveal?.reveal(element));
    }

    destroyMotion() {
      this.designTitleObserver?.disconnect();
      this.designTitleObserver = null;
      this.clockReveal?.destroy();
      this.clockReveal = null;
      (this.animations || []).forEach(animation => {
        animation.scrollTrigger?.kill(true);
        animation.kill();
      });
      this.animations = [];
      this.designTimeline = null;
      this.designActive = this.designExpanded = false;
      this.removeAttribute('data-design-story');
      this.style.removeProperty('--rs7-launch-copy-top');
      this.style.removeProperty('--rs7-launch-hero-top');
      const targets = [this.media, ...(this.mediaLayers || []), this.heroTitle, this.designTitle, this.designCopy, this.scrollCue].filter(Boolean);
      if (targets.length && window.gsap) window.gsap.set(targets, { clearProps: 'transform,opacity,visibility' });
      else targets.forEach(target => {
        ['transform', 'opacity', 'visibility'].forEach(property => target.style.removeProperty(property));
      });
    }

    refresh() {
      if (!this.isConnected) return;
      this.pager?.prepareRefresh();
      const layout = this.readStoryLayout();
      const shouldUseStory = !!(layout && !this.motionQuery.matches && !this.editor && window.gsap && window.ScrollTrigger);
      if (shouldUseStory !== this.hasAttribute('data-design-story') && !this.updatingMotion) {
        this.updateMotion();
        return;
      }
      if (shouldUseStory && this.hasAttribute('data-design-story')) {
        const changed = layout.height !== this.storyLayout?.height || layout.copyTop !== this.storyLayout?.copyTop
          || layout.lift !== this.storyLayout?.lift;
        this.storyLayout = layout;
        this.applyStoryLayout();
        if (changed && this.designTimeline) {
          const progress = this.designTimeline.progress();
          this.designTimeline.progress(0, true).invalidate().progress(progress, true);
        }
      }
      this.pager?.measureOverlap();
      this.animations.forEach(animation => animation.scrollTrigger?.refresh());
      this.syncDesignStep();
      this.revealDesignCopy();
      this.measureHeaderBoundary();
      this.updateVideo();
      this.requestScroll();
      this.pager?.refresh();
      this.chapters?.refresh();
    }

    videoSource() {
      const mobile = window.innerWidth < 750;
      const variant = mobile && this.mediaConfig.mobile?.sources?.length ? this.mediaConfig.mobile : this.mediaConfig.desktop;
      const sources = (variant?.sources || []).filter(source => source.url && /mp4/i.test(source.format || ''))
        .sort((a, b) => (Number(a.width) || 0) - (Number(b.width) || 0));
      const target = Math.min(window.innerWidth * Math.min(window.devicePixelRatio || 1, 1.5), mobile ? 1280 : 1920);
      return sources.find(source => Number(source.width) >= target)?.url || sources[sources.length - 1]?.url;
    }

    canPlayVideo() {
      return this.isConnected && this.heroVisible && !document.hidden
        && !this.motionQuery.matches && !this.editor && !this.videoFailed && !this.heroCovered;
    }

    stopVideo() {
      this.videoGeneration = (this.videoGeneration || 0) + 1;
      if (!this.video) return;
      this.video.pause();
      this.video.hidden = true;
      this.playPending = false;
    }

    updateVideo() {
      if (!this.video) return;
      if (!this.canPlayVideo()) {
        this.stopVideo();
        return;
      }
      const source = this.videoSource();
      if (!source) return;
      if (this.video.getAttribute('src') !== source) {
        this.stopVideo();
        this.video.src = source;
        this.video.muted = true;
        this.video.load();
      }
      if (this.playPending || !this.video.paused) return;
      const generation = this.videoGeneration;
      const video = this.video;
      this.playPending = true;
      Promise.resolve(video.play()).then(() => {
        if (generation !== this.videoGeneration) return;
        this.playPending = false;
        if (!this.canPlayVideo()) this.stopVideo();
      }).catch(() => {
        if (generation !== this.videoGeneration) return;
        this.videoFailed = true;
        this.stopVideo();
      });
    }

  });
}
